import type { InspirationTemplate } from '@shared/adTemplateMetadata';
import { adStyleOf } from './ad-style';

export type CanvasInspirationCard = { axis: 'style' | 'angle'; label: string; template: InspirationTemplate };
type Category = { axis: CanvasInspirationCard['axis']; label: string; templates: InspirationTemplate[] };
const normalizedLabel = (label: string) => label.trim().replace(/\s+/g, ' ').toLowerCase();

/** One representative for every available category, stable for a canvas seed. */
export function selectCanvasInspirationCards(templates: InspirationTemplate[], seed: number): CanvasInspirationCard[] {
  let state = seed >>> 0;
  const random = () => {
    state += 0x6D2B79F5;
    let value = Math.imul(state ^ state >>> 15, state | 1);
    value ^= value + Math.imul(value ^ value >>> 7, value | 61);
    return ((value ^ value >>> 14) >>> 0) / 4294967296;
  };
  const shuffle = <T,>(values: T[]): T[] => {
    for (let i = values.length - 1; i > 0; i--) {
      const j = Math.floor(random() * (i + 1));
      [values[i], values[j]] = [values[j], values[i]];
    }
    return values;
  };
  const categories = new Map<string, Category>();
  // Query results may arrive in a different order without changing the library.
  const canonical = [...templates].sort((a, b) => a.id - b.id || a.imageUrl.localeCompare(b.imageUrl)
    || a.name.localeCompare(b.name) || (a.sourceAdStyle ?? '').localeCompare(b.sourceAdStyle ?? '')
    || (a.sourceAdAngle ?? '').localeCompare(b.sourceAdAngle ?? ''));
  for (const template of canonical) {
    if (!template.isGlobal || template.mediaType === 'video' || !/^https:\/\/[^/]+\.b-cdn\.net\//i.test(template.imageUrl)) continue;
    const style = Object.prototype.hasOwnProperty.call(template, 'sourceAdStyle') ? template.sourceAdStyle : adStyleOf(template);
    for (const [axis, value] of [['style', style], ['angle', template.sourceAdAngle]] as const) {
      const label = value?.trim().replace(/\s+/g, ' ');
      if (!label) continue;
      const key = `${axis}:${normalizedLabel(label)}`;
      const category = categories.get(key) ?? { axis, label, templates: [] };
      // The same URL can carry different classifications on separate records.
      if (!category.templates.some(candidate => candidate.imageUrl === template.imageUrl)) category.templates.push(template);
      categories.set(key, category);
    }
  }
  const order = shuffle([...categories.entries()].sort(([a], [b]) => a.localeCompare(b)));
  for (const [, category] of order) shuffle(category.templates);
  const assigned = new Map<string, string>();
  const chosen = new Map<string, InspirationTemplate>();
  // Find the largest distinct-image assignment before repeating scarce images.
  const match = (key: string, visited: Set<string>): boolean => {
    for (const template of categories.get(key)!.templates) {
      if (visited.has(template.imageUrl)) continue;
      visited.add(template.imageUrl);
      const previous = assigned.get(template.imageUrl);
      if (!previous || match(previous, visited)) {
        assigned.set(template.imageUrl, key);
        chosen.set(key, template);
        return true;
      }
    }
    return false;
  };
  for (const [key] of order) match(key, new Set());
  return order.map(([key, category]) => ({ axis: category.axis, label: category.label,
    template: chosen.get(key) ?? category.templates[0] }));
}
